import { StopAddressType } from "types/MultiStop";

export type StopTimezoneOption = { value: string; label: string };

export type StopTimezoneQueryPlan = {
  filters: Record<string, string>;
  /** When true, a successful filtered response counts as Thaddeus/country-scoped for auto-first. */
  isFiltered: boolean;
  /** When true, run GET /common/timezones with `filters` (may be empty for ports). */
  useFilteredRequest: boolean;
};

type CountryLookupEntry = { id?: number | string; code?: string; label?: string };

export function resolveStopCountryCode(
  countryId: unknown,
  countryName: unknown,
  portCountry: unknown,
  countries: CountryLookupEntry[],
): string | undefined {
  if (countryId != null && countryId !== "") {
    const byId = countries.find((entry) => String(entry.id) === String(countryId));
    if (byId?.code) {
      return byId.code;
    }
  }

  const normalizedName = String(countryName || portCountry || "")
    .trim()
    .toLowerCase();
  if (!normalizedName) {
    return undefined;
  }

  const byName = countries.find((entry) => String(entry.label ?? "").trim().toLowerCase() === normalizedName);
  return byName?.code;
}

export function isPortStopAddressType(addressType: unknown): boolean {
  const normalized = Number(addressType);
  return normalized === StopAddressType.AIRPORT || normalized === StopAddressType.SEAPORT;
}

export function getStopTimezoneQueryPlan(params: {
  addressType: unknown;
  countryCode?: string;
  stateName?: string;
}): StopTimezoneQueryPlan {
  const { addressType, countryCode, stateName } = params;

  if (isPortStopAddressType(addressType)) {
    return { filters: {}, isFiltered: false, useFilteredRequest: true };
  }

  if (!countryCode) {
    return { filters: {}, isFiltered: false, useFilteredRequest: true };
  }

  return {
    filters: {
      country_code: countryCode,
      ...(stateName ? { state_name: String(stateName) } : {}),
    },
    isFiltered: true,
    useFilteredRequest: true,
  };
}

export function mapTimezoneApiOptions(data: { value: string; name: string }[]): StopTimezoneOption[] {
  return data.map((tz) => ({ value: tz.value, label: tz.name }));
}

function normalizeTimezoneValue(timezone?: string | null): string {
  return typeof timezone === "string" ? timezone.trim() : "";
}

function isMeaningfulTimezone(timezone?: string | null): boolean {
  const normalized = normalizeTimezoneValue(timezone);
  return Boolean(normalized) && normalized.toUpperCase() !== "UTC";
}

function timezoneInOptions(options: StopTimezoneOption[], timezone: string): boolean {
  return options.some((option) => option.value === timezone);
}

export function resolveStopTimezoneSelection(params: {
  options: StopTimezoneOption[];
  addressTimezone?: string | null;
  portTimezone?: string | null;
  currentTimezone?: string | null;
  isFiltered: boolean;
  hasAddressSource: boolean;
}): string | undefined {
  const { options, addressTimezone, portTimezone, currentTimezone, isFiltered, hasAddressSource } = params;
  if (!options.length) {
    return undefined;
  }

  const normalizedAddressTz = normalizeTimezoneValue(addressTimezone);
  const normalizedPortTz = normalizeTimezoneValue(portTimezone);
  const normalizedCurrentTz = normalizeTimezoneValue(currentTimezone);
  const currentIsInOptions = Boolean(normalizedCurrentTz) && timezoneInOptions(options, normalizedCurrentTz);

  if (isMeaningfulTimezone(normalizedPortTz)) {
    if (
      isMeaningfulTimezone(normalizedAddressTz) &&
      normalizedAddressTz !== normalizedPortTz &&
      timezoneInOptions(options, normalizedAddressTz)
    ) {
      return normalizedAddressTz;
    }
    return timezoneInOptions(options, normalizedPortTz) ? normalizedPortTz : undefined;
  }

  // Keep an already-selected option when the list changes (street → airport).
  // A newly picked, non-UTC address timezone still wins when it is in the list.
  if (currentIsInOptions) {
    if (
      isMeaningfulTimezone(normalizedAddressTz) &&
      normalizedAddressTz !== normalizedCurrentTz &&
      timezoneInOptions(options, normalizedAddressTz)
    ) {
      return normalizedAddressTz;
    }
    return normalizedCurrentTz;
  }

  if (normalizedAddressTz && timezoneInOptions(options, normalizedAddressTz)) {
    return normalizedAddressTz;
  }

  // Filtered (Thaddeus) list: UTC / any zone not in the options is treated as unset.
  if (isFiltered && hasAddressSource) {
    return options[0]?.value;
  }

  return undefined;
}

export function pickStopTimezoneOptions(params: {
  queryPlan: StopTimezoneQueryPlan;
  filteredOptions: StopTimezoneOption[] | undefined;
  filteredQueryFailed: boolean;
  fullListOptions: StopTimezoneOption[] | undefined;
}): { options: StopTimezoneOption[]; isFiltered: boolean } {
  const { queryPlan, filteredOptions, filteredQueryFailed, fullListOptions } = params;
  const fullList = fullListOptions ?? [];

  if (!queryPlan.useFilteredRequest || !queryPlan.isFiltered) {
    return { options: fullList, isFiltered: false };
  }

  if (!filteredQueryFailed && filteredOptions && filteredOptions.length > 0) {
    return { options: filteredOptions, isFiltered: true };
  }

  return { options: fullList, isFiltered: false };
}
